import { Component, OnInit } from '@angular/core';
import { BaseComponent, ErrorsUtil, InputComponent, MessageBoxComponent, OnEnterDirective, WaitButtonComponent, WaitComponent } from '@sircon/sircon-ui-framework';
import { ActivatedRoute } from '@angular/router';
import { HelpService } from 'src/services/help.service';
import { CarrierInformation } from 'src/app/core/models/carrier-information.model';
import { ProducerManagerIntegrationHttpService } from 'src/providers/producer-manager-integration-http.service';
import { catchError, finalize, of, takeUntil, tap } from 'rxjs';
import { InviteHttpService } from 'src/providers/invite-http.service';
import { ProducerSsoInviteRequest } from 'src/app/core/models/invite/producer-sso-invite-request.model';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrandInformationComponent } from '../brand-information/brand-information.component';

@Component({
    selector: 'app-sso-invite',
    templateUrl: './sso-invite.component.html',
    styleUrls: ['./sso-invite.component.less'],
    imports: [WaitComponent, NgClass, OnEnterDirective, MessageBoxComponent, InputComponent, FormsModule,
        WaitButtonComponent, BrandInformationComponent]
})
export class SsoInviteComponent extends BaseComponent implements OnInit {
    errors = ErrorsUtil.create();
    submitting = false;
    loadingBrand = false;
    brandKey: string;
    carrierInformation: CarrierInformation;
    samlConfirmationId: string;
    toEmailAddress: string;
    inviteEmailSource: string;
    inviteSent = false;
    inviteSentPreviously = false;
    inviteId: string;
    resendSuccessful = false;
    isAgency = false;
    firstName: string;
    lastName: string;

    constructor(
        private producerManagerIntegrationHttpService: ProducerManagerIntegrationHttpService,
        private inviteHttpService: InviteHttpService,
        private helpService: HelpService,
        private route: ActivatedRoute
    ) {
        super();
    }

    ngOnInit(): void {
        this.brandKey = this.route.snapshot.queryParams.brandKey;
        // Once the invite is sent we don't display brand info, but we still need it to load to get the carrierInformation
        this.loadingBrand = !!this.brandKey;
        this.inviteId = this.route.snapshot.queryParams.inviteId;
        this.inviteSentPreviously = !!this.route.snapshot.queryParams.inviteId;
        this.toEmailAddress = this.parseEmailParam(this.route.snapshot.queryParams.inviteEmail);
        this.inviteEmailSource = this.route.snapshot.queryParams.inviteEmailSource;
        this.samlConfirmationId = this.route.snapshot.queryParams.samlConfirmationId;
        this.firstName = this.route.snapshot.queryParams.firstName;
        this.lastName = this.route.snapshot.queryParams.lastName;
        this.isAgency = this.route.snapshot.queryParams.entityType === 'firm';
    }

    /**
     * When we receive an email param with a + sign, angular interprets it as a space.
     * Email addresses can only have a space in the local part if and only if enclosed in double quotes. (RFC5322).
     * Since this is not a practical scenario with most email providers, we will simply swap any spaces back to +
     * If such a scenario comes up in the future, we can do this more selectively for non-quoted space scenarios only.
     * @param emailParam which might have spaces
     */
    parseEmailParam(emailParam: string): string {
        return emailParam?.replace(/ /g, '+');
    }

    sendInvite(): void {
        this.submitting = true;
        ErrorsUtil.clear(this.errors);
        const request: ProducerSsoInviteRequest = {
            firstName: this.firstName,
            lastName: this.lastName,
            emailAddress: this.toEmailAddress
        };
        this.producerManagerIntegrationHttpService.createProducerSsoInvite(this.samlConfirmationId, request)
            .pipe(
                tap((response: { value: string }) => {
                    this.inviteId = response.value;
                    this.inviteSent = true;
                }),
                catchError((response) => {
                    this.errors = ErrorsUtil.mapResponse(response);
                    return of(null);
                }),
                finalize(() => {
                    this.submitting = false;
                }),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe();
    }

    brandedWalkupHelpUrl() {
        return this.helpService.getInsuranceHelpPath();
    }

    loadingBrandEvent(loading: boolean): void {
        this.loadingBrand = loading;
    }

    loadingBrandErrorEvent(): void {
        this.errors.serviceUnavailable = true;
    }

    carrierInformationEvent(carrierInformation: CarrierInformation): void {
        this.carrierInformation = carrierInformation;
    }

    startOver(): void {
        this.inviteSent = false;
        this.inviteSentPreviously = false;
    }

    resendInvite(): void {
        if (!this.submitting) {
            ErrorsUtil.clear(this.errors);
            this.submitting = true;
            this.resendSuccessful = false;

            this.inviteHttpService.resendInvite(this.inviteId)
                .pipe(
                    tap(() => {
                        this.resendSuccessful = true;
                    }),
                    catchError((response) => {
                        this.errors = ErrorsUtil.mapResponse(response);
                        return of(null);
                    }),
                    finalize(() => {
                        this.submitting = false;
                    }),
                    takeUntil(this.ngUnsubscribe)
                )
                .subscribe();
        }
    }
}
